/**
 * Hebrew gematria utilities — convert integers to traditional Hebrew
 * letter notation with the right gershayim and the קדושת השם guards
 * (15 → ט״ו, 16 → ט״ז; never י״ה / י״ו).
 *
 * Why JS-side: the browser's `Intl.DateTimeFormat("he-IL-u-ca-hebrew")`
 * gives Hebrew-calendar dates but with Arabic numerals
 * (e.g. "4 בסיון 5786") which look wrong on a Jewish religious
 * surface. This module gives us proper formatting like "ד׳ בסיון תשפ״ו".
 *
 * Year handling: years 5001-5999 (i.e. all of the 6th Hebrew
 * millennium) drop the implicit ה' (5000) prefix per the
 * convention used on printed religious documents. So 5786 → "תשפ״ו",
 * not "ה׳ תשפ״ו".
 *
 * Keeps the same conventions as `backend/services/hebrew_date.py`
 * so receipts (backend-rendered) and the donation thank-you page
 * (frontend-rendered) match exactly.
 */

const GERESH = "\u05F3";     // ׳ HEBREW PUNCTUATION GERESH
const GERSHAYIM = "\u05F4";  // ״ HEBREW PUNCTUATION GERSHAYIM

/**
 * Hebrew calendar month names in their canonical spelling. Index 0
 * is unused; 1 = Tishrei … 13 = Adar II (leap years only). For
 * regular years Adar lives at index 6.
 */
const HEBREW_MONTHS_NUMERIC = {
  1: "ניסן", 2: "אייר", 3: "סיון", 4: "תמוז",
  5: "אב",   6: "אלול", 7: "תשרי", 8: "חשוון",
  9: "כסלו", 10: "טבת", 11: "שבט",
  12: "אדר א׳", 13: "אדר ב׳",
};

/**
 * Map of `Intl.DateTimeFormat("he-IL-u-ca-hebrew", { month: "long" })`
 * outputs → canonical names. The browser tends to spell things slightly
 * differently between platforms ("חשון" vs "חשוון", "סיון" vs "סיוון")
 * so we normalise on the way through.
 */
const MONTH_ALIASES = {
  "ניסן": "ניסן",
  "אייר": "אייר",
  "סיון": "סיון", "סיוון": "סיון",
  "תמוז": "תמוז",
  "אב": "אב", "מנחם אב": "אב",
  "אלול": "אלול",
  "תשרי": "תשרי",
  "חשון": "חשוון", "חשוון": "חשוון", "מרחשון": "חשוון", "מרחשוון": "חשוון",
  "כסלו": "כסלו", "כסליו": "כסלו",
  "טבת": "טבת",
  "שבט": "שבט",
  "אדר": "אדר",
  "אדר א": "אדר א׳", "אדר א'": "אדר א׳", "אדר א׳": "אדר א׳",
  "אדר ב": "אדר ב׳", "אדר ב'": "אדר ב׳", "אדר ב׳": "אדר ב׳",
  "אדר ראשון": "אדר א׳", "אדר שני": "אדר ב׳",
};

/**
 * Convert an integer 1..999 to its Hebrew gematria letter string,
 * WITHOUT punctuation (gershayim is added separately so the caller
 * controls placement). 15 and 16 use the קדושת השם-safe spellings.
 *
 * Returns "" for n <= 0 (caller's problem).
 */
function intToHebrewLettersRaw(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  // Special-case the קדושת השם guards.
  if (n === 15) return "טו";
  if (n === 16) return "טז";

  let remaining = Math.floor(n);
  let out = "";

  // Hundreds (100..900). For 500..900 we use 400+rest (ת + ק/ר/ש/ת),
  // so 500=תק, 600=תר, 700=תש, 800=תת, 900=תתק.
  const hundredsLetters = ["", "ק", "ר", "ש", "ת"]; // 0,100,200,300,400
  while (remaining >= 400) {
    out += "ת";
    remaining -= 400;
  }
  if (remaining >= 100) {
    const h = Math.floor(remaining / 100);
    out += hundredsLetters[h];
    remaining -= h * 100;
  }

  // Tens 10..90. Inside this block we also hit the 15/16 guards.
  const tensLetters = {
    10: "י", 20: "כ", 30: "ל", 40: "מ", 50: "נ",
    60: "ס", 70: "ע", 80: "פ", 90: "צ",
  };
  // If the remaining two-digit chunk is 15 or 16, use טו/טז.
  if (remaining === 15) {
    out += "טו";
    remaining = 0;
  } else if (remaining === 16) {
    out += "טז";
    remaining = 0;
  } else if (remaining >= 10) {
    const t = Math.floor(remaining / 10) * 10;
    out += tensLetters[t] || "";
    remaining -= t;
  }

  // Units 1..9.
  const unitsLetters = {
    1: "א", 2: "ב", 3: "ג", 4: "ד", 5: "ה",
    6: "ו", 7: "ז", 8: "ח", 9: "ט",
  };
  if (remaining > 0) {
    out += unitsLetters[remaining] || "";
  }

  return out;
}

/**
 * Add traditional gershayim punctuation:
 *  - single-letter result → trailing geresh   (ה → ה׳)
 *  - multi-letter result  → gershayim before the last letter
 *                            (תשפו → תשפ״ו)
 */
function withGershayim(letters) {
  if (!letters) return "";
  if (letters.length === 1) return letters + GERESH;
  return letters.slice(0, -1) + GERSHAYIM + letters.slice(-1);
}


// =====================================================================
// Public API
// =====================================================================

/**
 * Hebrew day-of-month as gematria with the right punctuation.
 *   1 → "א׳", 4 → "ד׳", 15 → "ט״ו", 27 → "כ״ז", 30 → "ל׳"
 */
export function hebrewDayLetters(day) {
  const raw = intToHebrewLettersRaw(day);
  return withGershayim(raw);
}

/**
 * Hebrew year as gematria with gershayim, dropping the implicit
 * 5000 (ה') millennium prefix used on religious documents.
 *   5786 → "תשפ״ו"
 *   5784 → "תשפ״ד"
 */
export function hebrewYearLetters(year) {
  if (!Number.isFinite(year)) return "";
  // Drop the 5000 millennium — anything in the 5001..5999 range
  // is rendered as just the last three digits in gematria.
  const reduced = year > 5000 && year < 6000 ? year - 5000 : year;
  const raw = intToHebrewLettersRaw(reduced);
  return withGershayim(raw);
}

/**
 * Canonicalise a month name as returned by the browser Intl
 * formatter (or any other source) into the spelling we use across
 * the app. Pass-through for unknown names (rare diaspora variants).
 */
export function canonicalHebrewMonthName(name) {
  if (!name) return "";
  const key = name.trim();
  return MONTH_ALIASES[key] || key;
}

/**
 * Format a JS Date as a polished Hebrew-calendar string with
 * traditional gematria letters — matches the receipts/certificates
 * rendered by the backend.
 *
 *   new Date("2026-05-20") → "ד׳ בסיון תשפ״ו"
 *
 * Implementation: we ask `Intl.DateTimeFormat` to give us the
 * Hebrew-calendar parts (day-as-number, month name as string, year
 * as number), then re-render the day/year via our gematria helpers
 * and normalise the month name.
 *
 * Falls back to a Gregorian he-IL string if Intl can't produce
 * a Hebrew calendar (very old browsers).
 */
export function formatHebrewDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatToParts(date);

    let day = null;
    let month = "";
    let year = null;
    for (const p of parts) {
      if (p.type === "day") day = parseInt(p.value, 10);
      else if (p.type === "month") month = p.value;
      else if (p.type === "year") year = parseInt(p.value.replace(/\D+/g, ""), 10);
    }

    if (!day || !month || !year) {
      // Couldn't parse — fall back to Gregorian-in-Hebrew.
      return date.toLocaleDateString("he-IL");
    }

    const dayHe = hebrewDayLetters(day);
    const monthHe = canonicalHebrewMonthName(month);
    const yearHe = hebrewYearLetters(year);
    // "ב" prefix on the month is the standard religious-document
    // convention: "ד׳ בסיון תשפ״ו" (and not "ד׳ סיון תשפ״ו").
    return `${dayHe} ב${monthHe} ${yearHe}`;
  } catch {
    return date.toLocaleDateString("he-IL");
  }
}

// Exposed for tests and future Siddur features.
export const __testing = {
  intToHebrewLettersRaw,
  withGershayim,
  HEBREW_MONTHS_NUMERIC,
};

/* ============================================================
   English transliteration of the Hebrew date
   ------------------------------------------------------------
   Returns a string like "4 Sivan 5786" or "1 Adar I 5784" for use
   in EN-mode surfaces. Uses Standard Hebrew transliteration per the
   voice guide — never Yiddish forms ("Shabbos" / "Shevuos"). Year
   is rendered as a plain integer (no gematria). Falls back to the
   civil locale date if the browser can't resolve the Hebrew calendar.
   ============================================================ */
const HEBREW_MONTH_NAMES_EN = {
  "ניסן":   "Nissan",
  "אייר":   "Iyar",
  "סיון":   "Sivan",
  "תמוז":   "Tammuz",
  "אב":     "Av",
  "אלול":   "Elul",
  "תשרי":   "Tishrei",
  "חשוון":  "Cheshvan",
  "כסלו":   "Kislev",
  "טבת":    "Tevet",
  "שבט":    "Shevat",
  "אדר":    "Adar",
  "אדר א׳": "Adar I",
  "אדר ב׳": "Adar II",
};

export function formatHebrewDateEn(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatToParts(date);

    let day = null;
    let monthHe = "";
    let year = null;
    for (const p of parts) {
      if (p.type === "day") day = parseInt(p.value, 10);
      else if (p.type === "month") monthHe = p.value;
      else if (p.type === "year") year = parseInt(p.value.replace(/\D+/g, ""), 10);
    }
    if (!day || !monthHe || !year) {
      return date.toLocaleDateString("en-US", { dateStyle: "long" });
    }
    const canonical = canonicalHebrewMonthName(monthHe);
    const monthEn = HEBREW_MONTH_NAMES_EN[canonical] || canonical;
    return `${day} ${monthEn} ${year}`;
  } catch {
    return date.toLocaleDateString("en-US", { dateStyle: "long" });
  }
}

/**
 * Add gershayim/geresh to the LAST Hebrew word in `text` when that
 * word is a pure-letter gematria sequence missing punctuation
 * (e.g. "תהילים קמו" → "תהילים קמ״ו", "פרק ו" → "פרק ו׳").
 *
 * Detection rule: the trailing word must consist only of
 * Hebrew letters (no nikud) and must not already contain a
 * geresh (׳) or gershayim (״). Three letters or fewer is
 * matched to avoid touching common Hebrew words. Two-letter
 * words get a single gershayim before the last letter.
 *
 * Used in the Siddur for psalm headings supplied by Sefaria
 * (`תהילים קמו`) so they read like a printed siddur.
 */
export function ensureGematriaGershayim(text) {
  if (!text || typeof text !== "string") return text;
  // Already has Hebrew punctuation → assume the title is already
  // properly formatted by the source and leave it alone.
  if (text.includes(GERESH) || text.includes(GERSHAYIM)) return text;
  const parts = text.split(/\s+/);
  if (parts.length < 1) return text;
  const last = parts[parts.length - 1];
  // 1–3 Hebrew letters, no other characters.
  if (!/^[\u05D0-\u05EA]{1,3}$/.test(last)) return text;
  parts[parts.length - 1] = withGershayim(last);
  return parts.join(" ");
}

/**
 * Convert any Arabic digit run inside `text` to its Hebrew gematria
 * letter form with gershayim. Numbers above 999 are kept as-is
 * (rare in religious sources).
 *   "Psalm 146"     → "Psalm קמ״ו"
 *   "פרק 6 פסוק 26" → "פרק ו׳ פסוק כ״ו"
 */
export function digitsToHebrewGematria(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(/\d+/g, (m) => {
    const n = parseInt(m, 10);
    if (!Number.isFinite(n) || n <= 0 || n > 999) return m;
    return hebrewDayLetters(n);
  });
}
